import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import { ConfirmationDialog, PageHeader, useToast } from 'components/admin';
import { Button, Input, Select, Table } from 'components/common';
import { deleteProductSolutionContent, getAdminProductSolutionContents } from 'apis/adminProductSolutionContents';
import { getAdminProductSolutionCategories } from 'apis/adminProductSolutionCategories';
import { useActiveLanguagesQuery, useAdminDeleteMutation, useAdminListQuery } from 'hooks';
import '../../../styles/admin-shared.scss';

const ProductSolutionContentsList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize] = useState(10);
    const [keyword, setKeyword] = useState('');
    const [isActiveFilter, setIsActiveFilter] = useState('');
    const [languageCodeFilter, setLanguageCodeFilter] = useState('vi');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [deleteId, setDeleteId] = useState(null);
    const toast = useToast();
    const navigate = useNavigate();

    const { data: languages = [] } = useActiveLanguagesQuery();
    const { data: categoriesData = { items: [] } } = useAdminListQuery('product-solution-categories-options', { pageSize: 1000, languageCode: languageCodeFilter || 'vi' }, getAdminProductSolutionCategories);
    const categories = categoriesData.items;
    const categoryMap = useMemo(() => {
        const map = {};
        categories.forEach((cat) => {
            map[cat.id] = cat.name || `Danh mục #${cat.id}`;
        });
        return map;
    }, [categories]);
    const queryParams = useMemo(() => ({
        pageNumber: currentPage,
        pageSize,
        keyword: keyword || undefined,
        isActive: isActiveFilter === '' ? undefined : isActiveFilter === 'true',
        languageCode: languageCodeFilter || undefined,
        productSolutionCategoryId: categoryFilter || undefined,
    }), [currentPage, pageSize, keyword, isActiveFilter, languageCodeFilter, categoryFilter]);
    const { data = { items: [], total: 0, currentPage, pageSize }, isFetching, error } = useAdminListQuery('product-solution-contents', queryParams, getAdminProductSolutionContents);
    const deleteMutation = useAdminDeleteMutation('product-solution-contents', deleteProductSolutionContent);

    useEffect(() => { setCurrentPage(1); }, [keyword, isActiveFilter, languageCodeFilter, categoryFilter]);
    useEffect(() => { if (error) toast.error(error.message || 'T?i d? li?u th?t b?i'); }, [error, toast]);

    const handleDelete = async () => {
        try {
            const res = await deleteMutation.mutateAsync(deleteId);
            if (res?.success) {
                toast.success('Xóa thành công');
            } else {
                toast.error(res?.message || 'Xóa thất bại');
            }
        } catch {
            toast.error('Xóa thất bại');
        }
        setDeleteId(null);
    };

    const columns = [
        { field: 'id', header: 'ID' },
        {
            header: 'Danh mục',
            render: (row) => (
                <strong style={{ display: 'inline-block' }}>
                    {row.categoryName || categoryMap[row.productSolutionCategoryId] || '—'}
                </strong>
            ),
        },
        // { header: 'Mô tả', render: (row) => <span style={{ color: '#888', fontSize: 13 }}>{row.description ? (row.description.length > 80 ? row.description.slice(0, 80) + '...' : row.description) : '—'}</span> },
        // {
        //     header: 'Hình ảnh',
        //     render: (row) => row.images?.length > 0
        //         ? (
        //             <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        //                 <img src={row.images[0].imageUrl} alt="img" style={{ width: 40, height: 40, objectFit: 'cover', borderRadius: 4 }} />
        //                 {row.images.length > 1 && <span style={{ fontSize: 12, color: '#555' }}>+{row.images.length - 1}</span>}
        //             </div>
        //         )
        //         : <span style={{ color: '#ccc', fontSize: 12 }}>Chưa có</span>,
        // },
        {
            header: 'Trạng thái',
            render: (row) => (
                <span className={`status-badge ${row.isActive ? 'active' : 'inactive'}`}>
                    {row.isActive ? 'Hoạt động' : 'Không hoạt động'}
                </span>
            ),
        },
        {
            header: 'Thao tác',
            render: (row) => (
                <div className="action-buttons">
                    <button className="icon-btn icon-btn--edit" onClick={() => navigate(`/admin/product-solution-contents/edit/${row.id}`)} title="Sửa"><FiEdit2 size={15} /></button>
                    <button className="icon-btn icon-btn--delete" onClick={() => setDeleteId(row.id)} title="Xóa"><FiTrash2 size={15} /></button>
                </div>
            ),
        },
    ];

    return (
        <div className="admin-page">
            <PageHeader
                title="Nội dung Sản phẩm / Giải pháp"
                subtitle="Quản lý nội dung chi tiết sản phẩm và giải pháp"
                action={<Button onClick={() => navigate('/admin/product-solution-contents/create')}>+ Thêm Nội dung</Button>}
            />
            <div className="filters-bar">
                <div className="filter-item">
                    <Input placeholder="Tìm kiếm theo mô tả..." value={keyword} onChange={e => setKeyword(e.target.value)} />
                </div>
                <div className="filter-item">
                    <Select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả danh mục' },
                            ...categories.map(c => ({ value: c.id, label: c.name || `Danh mục #${c.id}` })),
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select value={isActiveFilter} onChange={e => setIsActiveFilter(e.target.value)}
                        options={[
                            { value: '', label: 'Tất cả trạng thái' },
                            { value: 'true', label: 'Hoạt động' },
                            { value: 'false', label: 'Không hoạt động' },
                        ]}
                    />
                </div>
                <div className="filter-item">
                    <Select value={languageCodeFilter} onChange={e => setLanguageCodeFilter(e.target.value)}
                        options={languages.map(l => ({ value: l.code, label: l.name }))}
                    />
                </div>
            </div>
            <Table
                columns={columns}
                data={data.items}
                loading={isFetching}
                pagination={{
                    currentPage: data.currentPage,
                    totalPages: Math.ceil(data.total / data.pageSize) || 1,
                    onPageChange: setCurrentPage,
                }}
            />
            <ConfirmationDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={handleDelete}
                title="Xóa Nội dung Sản phẩm / Giải pháp"
                message="Bạn có chắc chắn muốn xóa nội dung này không?"
            />
        </div>
    );
};

export default ProductSolutionContentsList;
